import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import { YMaps, Map, SearchControl, GeolocationControl } from 'react-yandex-maps';
import { drop, join, path, pipe, split } from 'ramda';
import PropTypes from 'prop-types';
import Button from '../../Navigation/Components/Button';
import Pin from '../../SvgIcons/CustomIcons/Pin';
import { useTranslate } from '../../../helpers/translate';
import Loader from '../../Loader';

const CENTER = [41.30882292588138, 69.25220409208157];
export const YANDEX_API_KEY = 'bc8fdb09-3efc-4819-8ca5-2c1d7f7708d2';

const buttonStyles = {
  marginBottom: '20px',
  zIndex: '2',
};

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  height: 100%;
`;

const PinUI = styled.div`
  position: absolute;
  bottom: calc(50% - 34px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  & svg {
    width: 30px;
    height: 64px;
  }
`;

const AddressBar = styled.div`
  max-width: ${props => (props.loading ? '45px' : '700px')};
  transition: max-width 300ms;
  position: absolute;
  z-index: 10;
  bottom: 15px;
  user-select: none;
  padding: 10px 20px;
  background: #fff;
  border-radius: 8px;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 4px 15px 0 rgba(190, 195, 204, 0.77);
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LAT = 0;
const LON = 1;

const YandexMap = (props) => {
  const { t } = useTranslate();
  const { input, onToggle, onAddressChange, addressValue, initialLocation } = props;

  const [mapReady, setMapReady] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [address, setAddress] = useState(null);

  const [center, setCenter] = useState(
    initialLocation?.lat && initialLocation?.lon
      ? [initialLocation.lat, initialLocation.lon]
      : CENTER
  );

  useEffect(() => {
    if (initialLocation?.lat && initialLocation?.lon) {
      setCenter([initialLocation.lat, initialLocation.lon]);
    }
  }, [initialLocation]);

  const onAddress = (coords) => {
    setAddressLoading(true);
    map.geocode(coords)
      .then((res) => {
        const firstGeoObject = res.geoObjects.get(0);
        const addressText = firstGeoObject.getAddressLine();
        const withoutCountry = pipe(split(','), drop(1), join(','))(addressText);
        onAddressChange(withoutCountry);

        const location = {
          lat: coords[LAT],
          lon: coords[LON],
        };
        input.onChange(location);
        setAddressLoading(false);
      })
      .catch(() => setAddressLoading(false));
  };

  useEffect(() => {
    if (address && map) {
      onAddress(address);
    }
  }, [address]);

  const onCenter = (val) => {
    const newCenter = path(['originalEvent', 'newCenter'], val);
    setAddress(newCenter);
  };

  const handleMapLoad = (ymaps) => {
    setMapReady(true);
    setMap(ymaps);
  };

  return (
    <MapWrapper>
      {!mapReady && (
        <LoaderOverlay>
          <Loader />
        </LoaderOverlay>
      )}

      <PinUI>
        <Pin />
      </PinUI>

      {!(!addressValue && !addressLoading) && (
        <AddressBar loading={addressLoading} id={'addressBar'}>
          {addressLoading ? '...' : addressValue}
        </AddressBar>
      )}

      <Button onClick={onToggle} title={t('continue')} style={{ ...buttonStyles }} />

      <YMaps query={{ apikey: YANDEX_API_KEY, lang: 'ru_RU' }} preload>
        <Map
          height="380px"
          width="100%"
          onLoad={handleMapLoad}
          onBoundsChange={onCenter}
          modules={['geocode']}
          defaultState={{ center, zoom: 15, controls: [] }}
        >
          <SearchControl options={{ float: 'left' }} />
          <GeolocationControl options={{ float: 'left' }} />
        </Map>
      </YMaps>
    </MapWrapper>
  );
};

YandexMap.propTypes = {
  input: PropTypes.any,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  onAddressChange: PropTypes.func,
  addressValue: PropTypes.any,
};

export default YandexMap;
